import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { WorkItemControllerService } from '@rapider/sdk';
import { WorkitemsActions } from './workitems.actions';

@Injectable()
export class WorkitemsEffects {
  private actions$ = inject(Actions);
  private workItemService = inject(WorkItemControllerService);

  loadWorkitems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkitemsActions.loadWorkitems),
      switchMap(({ filters }) => {
        const where: any = {};

        if (filters?.kind && filters.kind !== 'all') {
          if (filters.kind === 'needsAttention') {
            where.needsAttention = true;
          } else {
            where.kind = filters.kind;
          }
        }

        if (filters?.applicationId && filters.applicationId !== 'all') {
          where['source.typeId'] = filters.applicationId;
        }

        const filterObj = {
          where,
          limit: 20, // Requirement 5: limit last 20 work items
          order: ['occurredAt DESC'],
          fields: {
            id: true,
            title: true,
            summary: true,
            kind: true,
            status: true,
            needsAttention: true,
            occurredAt: true,
            source: true,
            priority: true,
            recommendation: true,
            bodyMarkdown: true,
            isDeleted: true,
          },
        };

        return this.workItemService
          .find({ filter: filterObj as any })
          .pipe(
            map((items: any[]) => {
              const active = Array.isArray(items)
                ? items.filter((i) => !i.isDeleted)
                : [];
              return WorkitemsActions.loadWorkitemsSuccess({
                workitems: active,
              });
            }),
            catchError((err) =>
              of(
                WorkitemsActions.loadWorkitemsFailure({
                  error: err?.message || 'Failed to load workitems',
                }),
              ),
            ),
          );
      }),
    ),
  );

  loadWorkitemDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkitemsActions.loadWorkitemDetail),
      switchMap(({ id }) =>
        this.workItemService.findById({ id }).pipe(
          map((workitem) =>
            WorkitemsActions.loadWorkitemDetailSuccess({ workitem }),
          ),
          catchError((err) =>
            of(
              WorkitemsActions.loadWorkitemDetailFailure({
                id,
                error: err?.message || 'Failed to load workitem details',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  updateWorkitemStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkitemsActions.updateWorkitemStatus),
      switchMap(({ id, status }) =>
        this.workItemService
          .updateById({
            id,
            body: { status },
          })
          .pipe(
            map(() =>
              WorkitemsActions.updateWorkitemStatusSuccess({ id, status }),
            ),
            catchError((err) =>
              of(
                WorkitemsActions.updateWorkitemStatusFailure({
                  id,
                  previousStatus: 'pending',
                  error: err?.message || 'Failed to update workitem status',
                }),
              ),
            ),
          ),
      ),
    ),
  );
}
